import * as icons from '../../assets/icons'
import { ActionDataWidgets } from './types'
import {
  AllHoursProps,
  FormFields,
  HoursProps,
  HoursType,
  StoreHoursType,
  TIME_ZERO
} from './StoreHours/StoreHours.types'
import { Card } from '@dtx-company/shared-components/src/components/atoms/Card/index'
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox, Select, Stack, Switch, TextField } from '@dtx-company/design-system/src'
import { Days } from './types/days'
import { DefaultManager, ManagerProps } from './components/Manager/Manager'
import { ExpandableCard } from './components/Flowpage'
import { FlowpageProps } from './types/FlowpageProps'
import { FormProps, StyledFormV2 } from './components/Layout'
import { FormTitle } from './components/Title'
import { HoursDisplay } from './StoreHours/HoursDisplay'
import { LinkStyleGated } from './components/LinkStyle/LinkStyleGated'
import { PunchClock, Remove } from '@mui/icons-material'
import { SettingsAndSaveLink } from './components/Settings'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { defaultWidgetLink } from '../../constants'
import { formatTime, getCurrentDayAbbrev } from '../../utils/time'
import { getLinkCardStyles } from '../flowpage/FlowpageLinkCard/utils/getLinkCardStyles'
import { getValidatedActionData, getValidatedActionDataOrThrow } from './typeUtils'
import { indexInterfaceWithVar } from '@dtx-company/true-common/src/utils/objects'
import { isClosed } from './StoreHours/StoreHours.hooks'
import { tzOptions } from '@dtx-company/app/shared/common/src/constants/timezones'
import { useFlowpageAnalyticsCollector } from '../../hooks/useFlowpageAnalyticsCollector'
import { useFlowpageTheme } from '../profile/Customize/hooks'
import { useForm } from 'react-hook-form-deprecated'
import { useLinkThemeState } from './components/LinkStyle/hooks/useLinkThemeState'
import { useSetValueWithPreview } from './utils'
import { useSubmitWidget } from './submitUtils'
import { v4 as uuid } from 'uuid'
import { weekdays } from './StoreHours/constants'

const defaultHours = { open: TIME_ZERO, close: TIME_ZERO, active: true }

const defaultStoreHours = weekdays.reduce(
  (acc, day) => ({
    ...acc,
    [day]: {
      ...defaultHours
    }
  }),
  {}
) as StoreHoursType
const Hours: FC<HoursProps> = ({
  key,
  onChange,
  label,
  values,
  disabled = false,
  initialValues,
  day,
  onCheck
}: HoursProps) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack flexGrow={1}>
        <Checkbox onChange={() => onCheck(day)} checked={!disabled} label={label.toUpperCase()} />
      </Stack>
      <TextField
        label="Open from"
        value={values?.open}
        type="time"
        onChange={e => {
          onChange(e.target.value, 'open', day)
        }}
        defaultValue={initialValues?.open || TIME_ZERO}
      />
      <Remove />
      <TextField
        type="time"
        label="Open till"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.value, 'close', day)
        }}
        value={values?.close}
        defaultValue={initialValues?.close || TIME_ZERO}
      />
    </Stack>
  )
}

const AllHours: FC<AllHoursProps> = ({ onChange, values, initialValues }: AllHoursProps) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <TextField
        fullWidth
        label="Open from"
        value={values?.open}
        type="time"
        onChange={e => {
          onChange(e.target.value, 'open')
        }}
        defaultValue={initialValues?.open || TIME_ZERO}
      />
      <Remove />
      <TextField
        fullWidth
        type="time"
        label="Open till"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.value, 'close')
        }}
        value={values?.close}
        defaultValue={initialValues?.close || TIME_ZERO}
      />
    </Stack>
  )
}

export const Form: FC<FormProps> = ({ widgetObj, order, curr, handleClose }: FormProps) => {
  const id = curr ? curr.id : uuid()

  const { linkTheme, setLinkTheme } = useLinkThemeState({
    defaultLinkTheme: curr?.linkTheme ?? null
  })
  const actionData = getValidatedActionData<'storeHours'>(curr?.actionData, 'hours')

  const defaultTimeZone = curr
    ? actionData?.timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone

  const {
    register,
    setValue: setFormValue,
    handleSubmit,
    watch
  } = useForm({
    defaultValues: {
      hours: curr ? actionData?.hours : defaultStoreHours,
      isSameHours: curr ? actionData?.isSameHours : true,
      timeZone: defaultTimeZone
    }
  })

  useEffect(() => {
    register(FormFields.HOURS)
    register(FormFields.SAME_HOURS)
    register(FormFields.TIMEZONE)
  }, [register])

  const submitWidget = useSubmitWidget()
  const onSubmit = async ({
    hours,
    isSameHours,
    timeZone
  }: {
    hours: StoreHoursType
    isSameHours: boolean
    timeZone: string
  }): Promise<void> => {
    await submitWidget({
      curr,
      actionData: { hours, isSameHours, timeZone },
      handleClose,
      linkTheme,
      widgetType: (curr ? curr.type : widgetObj.type) as ActionDataWidgets,
      fields: {
        title: '',
        description: '',
        id,
        order
      }
    })
  }

  const watchAll = watch()
  const hours: StoreHoursType = watchAll?.hours || defaultStoreHours

  const previewLink = useMemo(
    () => ({
      ...defaultWidgetLink,
      linkTheme,
      type: 'storeHours',
      id,
      actionData: {
        timeZone: watchAll.timeZone ?? 'America/New_York',
        isSameHours: Boolean(watchAll.isSameHours),
        hours
      }
    }),
    [hours, id, linkTheme, watchAll.isSameHours, watchAll.timeZone]
  )

  const { setValue, setLinkThemeValue } = useSetValueWithPreview(
    previewLink,
    setFormValue,
    setLinkTheme
  )

  const handleHoursChange = (time: string, type: 'open' | 'close', day: Days): void => {
    const currDay: HoursType = indexInterfaceWithVar(hours, day) || defaultHours

    const newHours = {
      ...currDay,
      [type]: time
    }
    setValue(FormFields.HOURS, { ...hours, [day]: newHours })
  }
  const handleAllHoursChange = useCallback(
    (time: string, type: 'open' | 'close'): void => {
      const newHours = Object.keys(hours).reduce(
        (week, day) => ({
          ...week,
          [day]: {
            ...hours[day as Days],
            [type]: time,
            active: true
          }
        }),
        {}
      )
      setValue(FormFields.HOURS, newHours)
    },
    [hours, setValue]
  )

  const newHours = Object.keys(hours).reduce(
    (week, day) => ({
      ...week,
      [day]: {
        open: hours.mon.open,
        close: hours.mon.close,
        active: true
      }
    }),
    {}
  )

  const toggleSameHours = useCallback((): void => {
    if (!watchAll.isSameHours) {
      setValue(FormFields.HOURS, newHours)
    }

    setValue(FormFields.SAME_HOURS, !watchAll.isSameHours)
  }, [newHours, setValue, watchAll.isSameHours])

  const toggleActiveDay = useCallback(
    (day: Days): void => {
      const currDay: HoursType = indexInterfaceWithVar(hours, day) || defaultHours

      setValue(FormFields.HOURS, {
        ...hours,
        [day]: {
          ...currDay,
          active: !currDay?.active
        }
      })
    },
    [hours, setValue]
  )

  const edit = Boolean(curr)

  return (
    <StyledFormV2 onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        <FormTitle icon={<PunchClock />} title={`${edit ? 'Edit these' : 'Add'} store hours`} />

        <Select
          label="Timezone"
          options={tzOptions}
          onChange={e => {
            setValue(FormFields.TIMEZONE, e.target.value)
          }}
          defaultValue={defaultTimeZone}
        />

        <Switch
          label="Use same hours for all days"
          defaultChecked={true}
          checked={Boolean(watchAll.isSameHours)}
          onChange={toggleSameHours}
        />

        {watchAll.isSameHours ? (
          <AllHours
            values={hours.mon || defaultHours}
            initialValues={curr ? (actionData?.hours.mon as HoursType) : null}
            onChange={handleAllHoursChange}
          />
        ) : (
          weekdays.map((day, idx) => (
            <Hours
              label={day}
              values={indexInterfaceWithVar(hours, day) || defaultHours}
              day={day}
              initialValues={curr ? (actionData?.hours[day] as HoursType) : null}
              key={idx}
              disabled={!hours[day]?.active}
              onCheck={toggleActiveDay}
              onChange={handleHoursChange}
            />
          ))
        )}

        <LinkStyleGated linkTheme={linkTheme} setLinkTheme={setLinkThemeValue} />

        <SettingsAndSaveLink disabled={false} curr={curr} handleClose={handleClose} />
      </Stack>
    </StyledFormV2>
  )
}

export const Flowpage: FC<FlowpageProps> = ({
  link,
  preview = false,
  editLinkMode = false,
  isLockedTemplateLink
}: FlowpageProps) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [clicked, setClicked] = useState<boolean>(false)
  const { trackLinkClick } = useFlowpageAnalyticsCollector({
    isPreview: preview,
    isEditMode: editLinkMode
  })
  const { theme } = useFlowpageTheme()

  const linkStyles = getLinkCardStyles({
    theme,
    linkTheme: link.linkTheme,
    isFeaturedLink: false
  })
  const toggleExpanded = (): void => {
    setExpanded(!expanded)
    if (!clicked) {
      trackLinkClick({ link_id: link.id }, { useHeap: true })
      setClicked(true)
    }
  }
  const actionData = getValidatedActionDataOrThrow<'storeHours'>(link.actionData, 'hours')
  const { hours, timeZone } = actionData

  const abbrevTimeZone =
    tzOptions.find(zone => {
      return zone.value === timeZone
    })?.label ?? ''
  const curDay = getCurrentDayAbbrev()
  const date = new Date()
  const d = new Date(
    date.toLocaleString('en-US', {
      timeZone: timeZone ?? 'America/New_York'
    })
  )
  const nowHours = d.getHours()
  const nowMins = d.getMinutes()
  const curDayHours = indexInterfaceWithVar(hours, curDay)
  const isClosedNow = isClosed(nowHours, nowMins, curDayHours || null)
  const defaultColor = link?.linkTheme?.active ? linkStyles.color : 'primary.black'

  return actionData?.empty ? (
    <Card pt="16px">
      <Text textAlign="center" variant="body/small" fontWeight={600} color="primary.black" mb="8px">
        Show what time your business is open
      </Text>
      <HoursDisplay
        todaysDay={curDay}
        isClosedNow={isClosedNow}
        defaultColor={defaultColor}
        abbrevTimeZone={abbrevTimeZone}
        hours={hours}
      />
    </Card>
  ) : (
    <ExpandableCard
      preview={preview}
      dataTestId="expandable-hours-card"
      expanded={expanded || Boolean(actionData.empty)}
      toggleExpanded={toggleExpanded}
      compactView={true}
      id={link.id}
      {...linkStyles}
      isLockedTemplateLink={isLockedTemplateLink}
      editLinkMode={editLinkMode}
      title={
        <>
          <Text fontWeight={600} fontSize="inherit" color="inherit" lineHeight="inherit">
            {isClosedNow ? 'Closed Now' : 'Open Now'}
          </Text>
          {curDayHours?.active && (
            <>
              <Text fontSize="inherit" fontWeight={600} color="inherit" lineHeight="inherit">
                {'\u2022'}
              </Text>
              <Text fontSize="inherit" lineHeight="inherit" color="inherit">
                {formatTime(curDayHours.open)} - {formatTime(curDayHours.close)}
              </Text>
            </>
          )}
        </>
      }
    >
      <HoursDisplay
        todaysDay={curDay}
        isClosedNow={isClosedNow}
        defaultColor={defaultColor}
        abbrevTimeZone={abbrevTimeZone}
        hours={hours}
      />
    </ExpandableCard>
  )
}

export const Manager: FC<ManagerProps> = ({ editWidget, link, handle }: ManagerProps) => {
  return (
    <DefaultManager
      iconUrl={icons.clock}
      altTitle="Store Hours"
      handle={handle}
      editWidget={editWidget}
      link={link}
    />
  )
}
